import React, { useEffect, useState } from 'react';
import { useApp, api, MEDIA } from '@/context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Trash2, Image as ImageIcon, Music, FileText } from 'lucide-react';
import { ArtifactView } from './AIStudioPage';
import { toast } from 'sonner';

export default function ResourceLibraryPage() {
  const { t, lang } = useApp();
  const [artifacts, setArtifacts] = useState([]);
  const [images, setImages] = useState([]);
  const [audio, setAudio] = useState([]);
  const [open, setOpen] = useState(null);

  const reload = () => {
    api.get('/ai/library/artifacts').then(r => setArtifacts(r.data));
    api.get('/ai/library/images').then(r => setImages(r.data));
    api.get('/ai/library/audio').then(r => setAudio(r.data));
  };
  useEffect(() => { reload(); }, []);

  const del = async (id) => {
    await api.delete(`/ai/library/artifacts/${id}`);
    toast.success(t('saved'));
    reload();
  };

  return (
    <main id="main" className="max-w-7xl mx-auto px-4 py-8" data-testid="library-page">
      <h1 className="text-3xl font-black mb-6">{t('resource_library')}</h1>
      <Tabs defaultValue="all" className="space-y-4">
        <TabsList className="bg-white border border-border">
          <TabsTrigger value="all" data-testid="tab-all"><FileText className="w-4 h-4 me-2" />{lang === 'ar' ? 'كل الموارد' : 'All'} ({artifacts.length})</TabsTrigger>
          <TabsTrigger value="images" data-testid="tab-images"><ImageIcon className="w-4 h-4 me-2" />{t('image_library')} ({images.length})</TabsTrigger>
          <TabsTrigger value="audio" data-testid="tab-audio"><Music className="w-4 h-4 me-2" />{t('audio_library')} ({audio.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="all">
          {artifacts.length === 0 ? (
            <Card><CardContent className="p-12 text-center text-muted-foreground">{t('no_results')}</CardContent></Card>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {artifacts.map(a => (
                <Card key={a.id} className="card-elevate cursor-pointer" data-testid={`artifact-${a.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="secondary" className="text-xs">{a.kind}</Badge>
                      <button onClick={(e) => { e.stopPropagation(); del(a.id); }} className="text-muted-foreground hover:text-destructive" aria-label="delete" data-testid={`del-artifact-${a.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <h3 className="font-bold text-sm line-clamp-2 mb-2">{a.title || a.name || a.topic || a.recipient_name || a.question || a.id}</h3>
                    <p className="text-xs text-muted-foreground mb-2">{new Date(a.created_at).toLocaleString(lang === 'ar' ? 'ar-AE' : 'en-US')}</p>
                    <Button size="sm" variant="outline" className="w-full" onClick={() => setOpen(a)} data-testid={`open-artifact-${a.id}`}>{t('open_artifact')}</Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="images">
          {images.length === 0 ? (
            <Card><CardContent className="p-12 text-center text-muted-foreground">{t('no_results')}</CardContent></Card>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {images.map((img, i) => (
                <a key={i} href={MEDIA(img.url)} target="_blank" rel="noreferrer" className="block group" data-testid={`lib-img-${i}`}>
                  <img src={MEDIA(img.url)} alt="" className="w-full aspect-square object-cover rounded-lg border border-border group-hover:border-primary transition-colors" loading="lazy" />
                  <p className="text-[10px] text-muted-foreground mt-1 line-clamp-1">{img.kind}</p>
                </a>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="audio">
          {audio.length === 0 ? (
            <Card><CardContent className="p-12 text-center text-muted-foreground">{t('no_results')}</CardContent></Card>
          ) : (
            <div className="space-y-2">
              {audio.map((a, i) => (
                <Card key={i} className="card-elevate" data-testid={`lib-audio-${i}`}>
                  <CardContent className="p-3 flex items-center gap-3">
                    <Badge variant="secondary" className="text-xs">{a.kind}</Badge>
                    <audio controls src={MEDIA(a.url)} className="flex-1" />
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="artifact-detail-dialog">
          <DialogHeader><DialogTitle>{open?.title || open?.name || open?.topic || open?.kind}</DialogTitle></DialogHeader>
          {open && <ArtifactView a={open} />}
        </DialogContent>
      </Dialog>
    </main>
  );
}
